import React, { useCallback } from "react"

import type { Department } from "@/models/department"
import { Site } from "@/models/site"
import { useUpdateSiteModel } from "@/services/site/useUpdateSite"
import { useConfirm } from "material-ui-confirm"

import { EditSiteConfirmContent } from "../../../components/edit-site-confirm-content/edit-site-confirm-content"
import type { SiteExplorerGridRecord } from "../../../services/use-get-site-explorer"

interface UseHandleProcessRowUpdateValue {
  handleProcessRowUpdate: (
    newValue: SiteExplorerGridRecord,
    value: SiteExplorerGridRecord
  ) => Promise<SiteExplorerGridRecord>
}

export const useHandleProcessRowUpdate = (
  departments: Department[] | undefined
): UseHandleProcessRowUpdateValue => {
  const confirm = useConfirm()

  const { siteUpdateMutateAsync } = useUpdateSiteModel()

  const handleProcessRowUpdate = useCallback(
    async (
      newValue: SiteExplorerGridRecord,
      value: SiteExplorerGridRecord
    ): Promise<SiteExplorerGridRecord> => {
      const buildingTypeIsDirty = newValue.building_type !== value.building_type
      const departmentNameIsDirty =
        newValue.department_name !== value.department_name
      const sqFtIsDirty = newValue.sq_ft !== value.sq_ft

      if (!buildingTypeIsDirty && !departmentNameIsDirty && !sqFtIsDirty) {
        return value
      }

      const { confirmed } = await confirm({
        title: "Confirm Updates",
        content: <EditSiteConfirmContent siteName={newValue.site_name} />,
        confirmationButtonProps: {
          color: "primary",
          variant: "contained",
        },
        confirmationText: "Confirm",
        cancellationButtonProps: { color: "primary", variant: "text" },
        cancellationText: "Cancel",
        dialogActionsProps: {
          disableSpacing: true,
        },
        dialogProps: {
          maxWidth: "xs",
        },
      })

      if (!confirmed) {
        return value
      }

      const { data: site } = await Site.includes(["department"])
        .selectExtra(["building_type", "sq_ft"])
        .find(value.id)

      const withRelationships = []

      const newDepartment = departments?.find(
        (department) => department.name === newValue.department_name
      )

      if (departmentNameIsDirty && newDepartment) {
        site.department = newDepartment
        withRelationships.push("department.id")
      }

      site.buildingType = newValue.building_type
      site.sqFt = newValue.sq_ft

      await siteUpdateMutateAsync({ site, with: withRelationships })

      return newValue
    },
    [confirm, departments, siteUpdateMutateAsync]
  )

  return {
    handleProcessRowUpdate,
  }
}
